import type { Component, ComponentProps } from "solid-js";

/**
 * Theme-aware inline SVG illustrations for empty states and headers.
 *
 * All strokes/fills use `currentColor` + opacity (never hardcoded hex), so the
 * drawing inherits whatever text color the caller sets (typically a token like
 * `text-accent`). They are decorative: callers provide the accessible label via
 * the surrounding empty-state copy, so the SVG is marked aria-hidden.
 */
type IllustrationProps = ComponentProps<"svg">;

/** A clipboard with a checklist — the core "inspection" motif. */
export const ClipboardChecklistIllustration: Component<IllustrationProps> = (props) => (
  <svg
    viewBox="0 0 160 160"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    {...props}
  >
    {/* Soft halo */}
    <circle cx="80" cy="80" r="64" fill="currentColor" opacity="0.06" />
    {/* Board */}
    <rect x="42" y="34" width="76" height="96" rx="10" fill="currentColor" opacity="0.1" />
    <rect
      x="42"
      y="34"
      width="76"
      height="96"
      rx="10"
      stroke="currentColor"
      stroke-width="3"
      opacity="0.5"
    />
    {/* Clip */}
    <rect x="66" y="26" width="28" height="16" rx="5" fill="currentColor" opacity="0.6" />
    {/* Checklist rows */}
    <g stroke="currentColor" stroke-width="3" stroke-linecap="round" opacity="0.45">
      <path d="M58 60 h12" />
      <path d="M58 78 h12" />
      <path d="M58 96 h12" />
    </g>
    <g stroke="currentColor" stroke-width="3" stroke-linecap="round" opacity="0.25">
      <path d="M80 60 h22" />
      <path d="M80 78 h22" />
      <path d="M80 96 h22" />
    </g>
    {/* Check ticks on the boxes */}
    <g stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
      <path d="M55 59 l2.5 2.5 l4 -5" opacity="0.9" />
      <path d="M55 77 l2.5 2.5 l4 -5" opacity="0.9" />
    </g>
  </svg>
);

/**
 * A broken/disconnected clipboard with a warning spark — the "something went
 * wrong" motif for error boundaries. Theme-aware via currentColor.
 */
export const BrokenClipboardIllustration: Component<IllustrationProps> = (props) => (
  <svg
    viewBox="0 0 160 160"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    {...props}
  >
    {/* Soft halo */}
    <circle cx="80" cy="80" r="64" fill="currentColor" opacity="0.06" />
    {/* Board, split down the middle */}
    <path
      d="M42 44 a10 10 0 0 1 10 -10 h14 l-6 96 h-8 a10 10 0 0 1 -10 -10 Z"
      fill="currentColor"
      opacity="0.1"
    />
    <path
      d="M118 44 a10 10 0 0 0 -10 -10 H94 l6 96 h8 a10 10 0 0 0 10 -10 Z"
      fill="currentColor"
      opacity="0.1"
    />
    <path
      d="M42 44 a10 10 0 0 1 10 -10 h14 l-6 96 h-8 a10 10 0 0 1 -10 -10 Z"
      stroke="currentColor"
      stroke-width="3"
      stroke-linejoin="round"
      opacity="0.5"
    />
    <path
      d="M118 44 a10 10 0 0 0 -10 -10 H94 l6 96 h8 a10 10 0 0 0 10 -10 Z"
      stroke="currentColor"
      stroke-width="3"
      stroke-linejoin="round"
      opacity="0.5"
    />
    {/* Clip */}
    <rect x="66" y="26" width="28" height="16" rx="5" fill="currentColor" opacity="0.6" />
    {/* Warning spark in the crack */}
    <path d="M82 58 l-8 22 h8 l-4 20 l14 -28 h-9 l5 -14 Z" fill="currentColor" opacity="0.85" />
  </svg>
);

/** A friendly shield-check for "all clear / nothing wrong" finding states. */
export const ShieldCheckIllustration: Component<IllustrationProps> = (props) => (
  <svg
    viewBox="0 0 160 160"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    {...props}
  >
    <circle cx="80" cy="80" r="64" fill="currentColor" opacity="0.06" />
    <path
      d="M80 30 L116 44 V82 C116 106 100 122 80 132 C60 122 44 106 44 82 V44 Z"
      fill="currentColor"
      opacity="0.1"
    />
    <path
      d="M80 30 L116 44 V82 C116 106 100 122 80 132 C60 122 44 106 44 82 V44 Z"
      stroke="currentColor"
      stroke-width="3"
      stroke-linejoin="round"
      opacity="0.5"
    />
    <path
      d="M64 80 l11 11 l22 -26"
      stroke="currentColor"
      stroke-width="5"
      stroke-linecap="round"
      stroke-linejoin="round"
      opacity="0.9"
    />
  </svg>
);
